import React, { useEffect, useState } from 'react';
import { PLAN_LABELS, getPlanDescription, type PlanId, type SubscriptionPlan } from '../../../domain/entities/Subscription';
import { fetchAvailablePlans } from '../../../services/modules/subscription/subscription.api';
import { cn } from '@/lib/utils';

interface RegisterPlanPickerProps {
  value: PlanId;
  onChange: (plan: PlanId) => void;
}

const PLAN_IDS = Object.keys(PLAN_LABELS) as PlanId[];

function formatPlanPrice(pricePerUser: number): string {
  return `R$ ${pricePerUser.toFixed(2).replace('.', ',')}`;
}

const RegisterPlanPicker: React.FC<RegisterPlanPickerProps> = ({ value, onChange }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchAvailablePlans()
      .then((data) => {
        if (!cancelled) setPlans(data);
      })
      .catch(() => {
        /* sem preços, o picker cai nos labels estáticos */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const priceOf = (id: PlanId): string | null => {
    const plan = plans.find((p) => p.id === id);
    return plan ? formatPlanPrice(plan.pricePerUser) : null;
  };

  return (
    <fieldset className="min-w-0">
      <legend id="plan-legend" className="mb-s2 flex flex-wrap items-center gap-s2">
        <span className="text-sm font-medium text-default">Plano</span>
        <span className="badge badge-brand">7 dias grátis</span>
      </legend>

      <div
        role="radiogroup"
        aria-labelledby="plan-legend"
        className="flex flex-col gap-s1"
      >
        {PLAN_IDS.map((id) => {
          const selected = id === value;
          const price = priceOf(id);
          const descriptionId = `plan-desc-${id}`;

          return (
            <div key={id}>
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                aria-describedby={selected ? descriptionId : undefined}
                onClick={() => onChange(id)}
                className={cn(
                  'flex h-11 w-full items-center gap-s3 rounded-md border px-s3 text-left transition-colors touch-manipulation',
                  'focus-visible:outline-none focus-visible:shadow-focus',
                  selected
                    ? 'border-brand-200 bg-brand-50'
                    : 'border-subtle bg-surface hover:border-brand-200 hover:bg-surface-subtle',
                )}
              >
                <span
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-full border',
                    selected ? 'border-primary bg-primary' : 'border-subtle bg-surface-0',
                  )}
                  aria-hidden
                >
                  {selected && <span className="size-1.5 rounded-full bg-surface-0" />}
                </span>

                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-default">
                  {PLAN_LABELS[id]}
                </span>

                {price && (
                  <span className="shrink-0 font-mono text-sm font-medium tabular-nums text-primary">
                    {price}
                    <span className="ml-s1 font-sans text-xs font-normal text-muted">/usuário</span>
                  </span>
                )}
              </button>

              {selected && (
                <p id={descriptionId} className="mt-s1 px-s3 text-sm text-muted">
                  {getPlanDescription(id)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

export default RegisterPlanPicker;
